/**
 * Cosmera — global.js
 * One small deferred script. No frameworks, no dependencies.
 * 1. Scroll reveals (IntersectionObserver, respects prefers-reduced-motion)
 * 2. Header state (shadow on scroll, mobile drawer close on nav)
 * 3. Quantity steppers (+ / −)
 * 4. Cart auto-update on quantity change
 * 5. Product form: variant resolution, price + availability updates
 */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* 1. Scroll reveals ------------------------------------------------ */
  function initReveals() {
    var targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var delay = entry.target.getAttribute('data-reveal-delay');
            if (delay) entry.target.style.transitionDelay = delay + 'ms';
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) { observer.observe(el); });
  }

  /* 2. Header --------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Close the mobile drawer when a link inside it is followed.
    var drawer = document.querySelector('[data-menu-drawer]');
    if (drawer) {
      drawer.addEventListener('click', function (event) {
        if (event.target.closest('a')) drawer.removeAttribute('open');
      });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && drawer.hasAttribute('open')) {
          drawer.removeAttribute('open');
          var summary = drawer.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    }
  }

  /* 3. Quantity steppers ------------------------------------------------ */
  function initQuantitySteppers() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-qty-change]');
      if (!button) return;

      var wrapper = button.closest('[data-qty]');
      var input = wrapper && wrapper.querySelector('input[type="number"]');
      if (!input) return;

      var step = button.getAttribute('data-qty-change') === 'up' ? 1 : -1;
      var min = parseInt(input.min, 10);
      if (isNaN(min)) min = 0;
      var next = (parseInt(input.value, 10) || 0) + step;
      if (next < min) next = min;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* 4. Cart: submit the cart form when a line quantity changes ----------- */
  function initCart() {
    var cartForm = document.querySelector('[data-cart-form]');
    if (!cartForm) return;

    var timer;
    cartForm.addEventListener('change', function (event) {
      if (!event.target.matches('[data-line-quantity]')) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        if (typeof cartForm.requestSubmit === 'function') cartForm.requestSubmit();
        else cartForm.submit();
      }, 400);
    });
  }

  /* 5. Product form ------------------------------------------------------- */
  function initProductForms() {
    document.querySelectorAll('[data-product-form-wrapper]').forEach(function (wrapper) {
      var dataEl = wrapper.querySelector('[data-variants-json]');
      if (!dataEl) return;

      var variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        return;
      }

      var idInput = wrapper.querySelector('[data-variant-id]');
      var priceEl = wrapper.querySelector('[data-price]');
      var buyButton = wrapper.querySelector('[data-add-to-cart]');
      var buyButtonText = wrapper.querySelector('[data-add-to-cart-text]');
      var moneyFormat = wrapper.getAttribute('data-money-format') || '{{amount}}';
      var strings = {
        add: wrapper.getAttribute('data-text-add') || 'Add to bag',
        soldOut: wrapper.getAttribute('data-text-sold-out') || 'Sold out',
        unavailable: wrapper.getAttribute('data-text-unavailable') || 'Unavailable'
      };

      function formatMoney(cents) {
        var amount = (cents / 100).toFixed(2);
        return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
      }

      function selectedOptions() {
        var options = [];
        wrapper.querySelectorAll('[data-option-index]').forEach(function (group) {
          var index = parseInt(group.getAttribute('data-option-index'), 10);
          var checked = group.querySelector('input[type="radio"]:checked');
          var select = group.querySelector('select');
          options[index] = checked ? checked.value : select ? select.value : null;
        });
        return options;
      }

      function currentVariant() {
        var options = selectedOptions();
        return variants.find(function (variant) {
          return variant.options.every(function (value, index) {
            return value === options[index];
          });
        });
      }

      function render() {
        var variant = currentVariant();

        if (!variant) {
          if (buyButton) buyButton.disabled = true;
          if (buyButtonText) buyButtonText.textContent = strings.unavailable;
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (priceEl) {
          var html = formatMoney(variant.price);
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            html += ' <s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>';
          }
          priceEl.innerHTML = html;
        }

        if (buyButton) buyButton.disabled = !variant.available;
        if (buyButtonText) {
          buyButtonText.textContent = variant.available ? strings.add : strings.soldOut;
        }

        if (window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      wrapper.addEventListener('change', function (event) {
        if (event.target.closest('[data-option-index]')) render();
      });
    });
  }

  function init() {
    initReveals();
    initHeader();
    initQuantitySteppers();
    initCart();
    initProductForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
